/**
 * UAVTradeZone - Notification Card Component
 * Date: 2026-02-09
 *
 * Displays a single notification with icon, title, timestamp, description,
 * and optional action button.
 */

import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Notification,
  getNotificationIcon,
  getNotificationTitle,
  getNotificationDescription,
  getNotificationActionUrl,
  getNotificationActionText,
} from "../../../interfaces/notification";
import "../styles/notifications.scss";

interface NotificationCardProps {
  notification: Notification;
}

/**
 * Format a date as relative time (e.g., "2 hours ago", "Yesterday", "Feb 5")
 */
function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHr = Math.floor(diffMin / 60);
  const diffDays = Math.floor(diffHr / 24);

  if (diffSec < 60) {
    return "Just now";
  }
  if (diffMin < 60) {
    return `${diffMin} minute${diffMin !== 1 ? "s" : ""} ago`;
  }
  if (diffHr < 24) {
    return `${diffHr} hour${diffHr !== 1 ? "s" : ""} ago`;
  }
  if (diffDays === 1) {
    return "Yesterday";
  }
  if (diffDays < 7) {
    return `${diffDays} days ago`;
  }

  // For older dates, show formatted date
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: now.getFullYear() !== date.getFullYear() ? "numeric" : undefined,
  });
}

const NotificationCard: React.FC<NotificationCardProps> = ({ notification }) => {
  const navigate = useNavigate();

  const icon = getNotificationIcon(notification.type);
  const title = getNotificationTitle(notification);
  const description = getNotificationDescription(notification);
  const actionUrl = getNotificationActionUrl(notification);
  const actionText = getNotificationActionText(notification.type);
  const isNew = !notification.dismissedAt;
  const relativeTime = formatRelativeTime(notification.createdAt);

  const handleActionClick = () => {
    if (actionUrl) {
      // Check if it's an external URL or internal route
      if (actionUrl.startsWith("http")) {
        window.open(actionUrl, "_blank", "noopener,noreferrer");
      } else {
        navigate(actionUrl);
      }
    }
  };

  return (
    <div className="notification-card">
      <div className="notification-card__icon-wrapper">
        <img
          src={icon}
          alt=""
          className="notification-card__icon"
          aria-hidden="true"
        />
        {isNew && <span className="notification-card__new-dot" aria-label="New" />}
      </div>

      <div className="notification-card__content">
        <div className="notification-card__header">
          <h4 className="notification-card__title u-P1-18-semi-bold">{title}</h4>
          <span className="notification-card__time u-Label-12 text-color-grey-1">
            {relativeTime}
          </span>
        </div>
        {description && (
          <p className="notification-card__description u-P3-14-medium text-color-grey-1">
            {description}
          </p>
        )}
      </div>

      {actionUrl && actionText && (
        <div className="notification-card__action">
          <button
            type="button"
            className="btn-figma-1 btn-sm"
            onClick={handleActionClick}
          >
            {actionText}
          </button>
        </div>
      )}
    </div>
  );
};

export default NotificationCard;
